import { t, Trans } from "@lingui/macro";
import {
  Autocomplete,
  Button,
  CircularProgress,
  CloseReason,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { importFiling } from "../../api/filings";
import { filingImportAllowedCountries, filingImportAllowedTaxonomyNames } from "../../helpers/constants";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

export interface CreateOrganizationDialogProps {
  open: boolean;
  onClose: (
    updateStatus: "none" | "success" | "error",
    reason?: string
  ) => void;
}

const FilingImportDialog = ({
  open,
  onClose,
}: CreateOrganizationDialogProps) => {
  const [currentFile, setCurrentFile] = useState<File>();
  const [country, setCountry] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [taxonomy, setTaxonomy] = useState("");
  const [saving, setSaving] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string>();
  const [currentFileInputKey, setCurrentFileInputKey] = useState(
    crypto.randomUUID()
  );

  const resetDialog = () => {
    setCountry("");
    setReportDate("");
    setTaxonomy("");
    setCurrentFileInputKey(crypto.randomUUID());
    setCurrentFile(undefined);
    setError("");
    setIsValid(false);
  };

  useEffect(() => {
    const isValid =
      currentFile !== undefined &&
      country !== "" &&
      reportDate !== "" &&
      taxonomy !== "";
    setIsValid(isValid);
  }, [currentFile, country, taxonomy, reportDate]);

  const handleFileSelected = (fileList: FileList | null) => {
    setError("");
    if (fileList) {
      const file = fileList.item(0);
      if (file && file.type.length > 0) {
        const ext = file.name.split(".")[1];
        if (
          ext &&
          (ext.toLowerCase() === "zip" || ext.toLowerCase() === "xbri")
        ) {
          setCurrentFile(file);
        } else {
          setError(
            t`Selected file has an invalid extension. please select another (.zip or .xbri)`
          );
        }
      }
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const controller = new AbortController();
      await importFiling(
        currentFile as File,
        country,
        taxonomy,
        reportDate,
        controller.signal
      );
      onClose("success", t`Filing imported`);
      setSaving(false);
      resetDialog();
    } catch (ex: any) {
      setSaving(false);
      resetDialog();
      setError(
        ex.response.data.IsTranslated
          ? ex.response.data.Exceptions[0]
          : t`Could not import filing. Try again later`
      );
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={() => false}
    >
      <>
        <DialogTitle>
          <Trans>Import Filing</Trans>
        </DialogTitle>
        <DialogContent>
          {saving && (
            <DialogContentText>
              <Trans>Hang in there, This might take a few minutes...</Trans>
            </DialogContentText>
          )}
          <Grid container spacing={2}>
            {!saving && (
              <>
                <Grid size={3}>
                  <Button variant="outlined" component="label">
                    <Trans>Select File</Trans>
                    <input
                      key={currentFileInputKey}
                      hidden
                      type="file"
                      multiple={false}
                      onChange={(e) => {
                        handleFileSelected(e.target.files);
                      }}
                    />
                  </Button>
                </Grid>
                <Grid size={8}>
                  <Typography>{currentFile?.name}</Typography>
                </Grid>
                {currentFile && (
                  <Grid size={1}>
                    <IconButton
                      edge="end"
                      onClick={() => setCurrentFile(undefined)}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Grid>
                )}
                <Grid size={12}>
                  <Autocomplete
                    sx={{ marginTop: 1 }}
                    options={filingImportAllowedCountries}
                    getOptionLabel={(option) => option.label}
                    onChange={(
                      event: any,
                      newValue: { code: string; label: string } | null
                    ) => {
                      setCountry(newValue?.code || "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Choose a country" />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Autocomplete
                    sx={{ marginTop: 1 }}
                    options={filingImportAllowedTaxonomyNames}
                    getOptionLabel={(option) => option.label}
                    onChange={(
                      event: any,
                      newValue: { code: string; label: string } | null
                    ) => {
                      setTaxonomy(newValue?.code || "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Choose a taxonomy" />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      format="YYYY-MM-DD"
                      label={t`Report Date`}
                      value={reportDate !== "" ? moment(reportDate) : moment()}
                      disableFuture
                      onChange={(newValue) => {
                        setReportDate(newValue?.format("YYYY-MM-DD") || "");
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        {error && (
          <DialogContentText sx={{ color: "red", textAlign: "center" }}>
            {error}
          </DialogContentText>
        )}
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              resetDialog();
              onClose("none");
            }}
          >
            <Trans>Close</Trans>
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !isValid}
          >
            {!saving && <Trans>Import</Trans>}
            {saving && <CircularProgress size={25} />}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  );
};

export default FilingImportDialog;
